import { makeAutoObservable, observable, runInAction } from "mobx";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import apiRequest from "../../utils/apiClient";
import { getVideoDurationSeconds } from "../../utils/videoUtils";
import { readLimitError, LimitLink } from "../../utils/limitErrorHandler";
import {
  presignedUrlEndpoint,
  updateUploadVideoData,
  MAX_VIDEO_SIZE_BYTES,
  SUPPORTED_VIDEO_FORMATS,
} from "../../components/constants";

export const LANGUAGE_KEY = "cc_upload_languages";
function readLanguages(): string[] {
  try {
    const v = JSON.parse(window.localStorage.getItem(LANGUAGE_KEY) || "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
  } catch (e) { return []; }
}

// Out of transcription hours / storage: the error carries a link to the plans page, and the
// message box shows it as "Subscribe →". This used to jump to /subscription/ 2.5 s later with
// no word of why — people took it for an ad and went back (2026-10-08).
async function planLimitError(response: Response): Promise<Error> {
  const { message, link } = await readLimitError(response);
  return Object.assign(new Error(message), { limitLink: link });
}

class UploadStore {
  file: File | null = null;
  projectTitle: string = "";
  uploadProgress: number = 0;
  isUploading: boolean = false;
  error: string | null = null;
  transcriptionEngine: string = "assemblyai";
  audioTrack: number = 1; // which audio track to transcribe (1 = default/first)
  // Which language(s) the speech is in. [] = auto-detect among English / Hebrew / Yiddish,
  // keeping mid-sentence switches. Remembered per browser so it never has to be chosen twice.
  languages: string[] = readLanguages();

  uploadEtaSeconds: number | null = null;
  uploadFinishing: boolean = false;

  batchCurrent: number = 0;
  batchTotal: number = 0;

  private _uploadStartTime: number | null = null;
  private _uploadStartBytes: number = 0;
  private _countdownInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    makeAutoObservable(this, {
      file: observable,
      projectTitle: observable,
      uploadProgress: observable,
      isUploading: observable,
      error: observable,
      transcriptionEngine: observable,
      audioTrack: observable,
      uploadEtaSeconds: observable,
      uploadFinishing: observable,
      batchCurrent: observable,
      batchTotal: observable,
    });
  }

  setLanguages = (langs: string[]) => {
    this.languages = langs;
    try { window.localStorage.setItem(LANGUAGE_KEY, JSON.stringify(langs)); } catch (e) { /* private mode */ }
  };

  validateFile = (fileToValidate: File): { isValid: boolean; error?: string } => {
    const fileType = fileToValidate.name.split(".").pop()?.toLowerCase();
    if (!fileType || !SUPPORTED_VIDEO_FORMATS.includes(fileType)) {
      return {
        isValid: false,
        error: `Invalid file type. Please upload a supported video format: ${SUPPORTED_VIDEO_FORMATS.join(", ")}`,
      };
    }
    if (fileToValidate.size > MAX_VIDEO_SIZE_BYTES) {
      return {
        isValid: false,
        error: `File size exceeds the limit. Please upload a smaller video.`,
      };
    }
    return { isValid: true };
  };

  setFile = (file: File | null) => {
    runInAction(() => {
      this.file = file;
      this.error = null;
    });
  };

  setProjectTitle = (title: string) => {
    runInAction(() => {
      this.projectTitle = title;
    });
  };

  setTranscriptionEngine = (engine: string) => {
    runInAction(() => {
      this.transcriptionEngine = engine;
    });
  };

  setAudioTrack = (track: number) => {
    runInAction(() => {
      this.audioTrack = track && track > 0 ? track : 1;
    });
  };

  private _clearCountdown = () => {
    if (this._countdownInterval !== null) {
      clearInterval(this._countdownInterval);
      this._countdownInterval = null;
    }
  };

  private _startCountdown = () => {
    this._clearCountdown();
    this._countdownInterval = setInterval(() => {
      runInAction(() => {
        if (this.uploadEtaSeconds !== null && this.uploadEtaSeconds > 1) {
          this.uploadEtaSeconds -= 1;
        } else {
          this.uploadFinishing = true;
        }
      });
    }, 1000);
  };

  private _resetUploadState = () => {
    this._clearCountdown();
    this.uploadProgress = 0;
    this.error = null;
    this.uploadEtaSeconds = null;
    this.uploadFinishing = false;
    this._uploadStartTime = null;
    this._uploadStartBytes = 0;
  };

  reset = () => {
    runInAction(() => {
      this._resetUploadState();
      this.file = null;
      this.projectTitle = "";
      this.isUploading = false;
      this.transcriptionEngine = "assemblyai";
      this.audioTrack = 1;
      this.batchCurrent = 0;
      this.batchTotal = 0;
    });
  };

  uploadBatch = async (
    files: File[],
    engine: string,
    onFileSuccess?: (msg: string) => void,
    onError?: (err: string, limitLink?: LimitLink) => void,
    onAllDone?: () => void,
  ) => {
    for (let i = 0; i < files.length; i++) {
      runInAction(() => {
        this.batchCurrent = i + 1;
        this.batchTotal = files.length;
        this.file = files[i];
        this.transcriptionEngine = engine;
      });
      const title = files[i].name.replace(/\.[^/.]+$/, "");
      await this.uploadFile(title, onFileSuccess, onError);
    }
    this.reset();
    onAllDone?.();
  };

  uploadFile = async (
    title: string,
    onSuccess?: (message: string) => void,
    onError?: (error: string, limitLink?: LimitLink) => void,
  ) => {
    if (!this.file) return;

    runInAction(() => {
      this._resetUploadState();
      this.isUploading = true;
    });

    try {
      const file = this.file;
      const uuid = uuidv4().toString();
      const fileExt = file.name.split(".").pop();
      const uuidFilename = `${uuid}.${fileExt}`;
      const fileTitle = title || file.name.replace(/\.[^/.]+$/, "");

      let durationSeconds: number;
      try {
        durationSeconds = await getVideoDurationSeconds(file);
      } catch {
        throw new Error("Could not read video duration.");
      }
      if (
        typeof durationSeconds !== "number" ||
        !Number.isFinite(durationSeconds) ||
        durationSeconds <= 0
      ) {
        throw new Error("Invalid video duration.");
      }

      const presignedUrlResponse = await apiRequest(presignedUrlEndpoint, {
        method: "POST",
        body: new URLSearchParams({
          filename: uuidFilename,
          title: fileTitle,
          size: file.size.toString(),
          duration_seconds: String(Math.round(durationSeconds)),
          uuid: uuid,
          original_filename: file.name,
        }),
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      if (presignedUrlResponse.status === 403) {
        throw await planLimitError(presignedUrlResponse);
      }

      if (!presignedUrlResponse.ok) {
        const errBody = await presignedUrlResponse
          .json()
          .catch(() => ({ error: "Failed to get upload URL" }));
        throw new Error(errBody.error || "Failed to get upload URL");
      }

      const presignedData = await presignedUrlResponse.json();
      if (!presignedData?.url) {
        throw new Error("Failed to get upload URL");
      }

      const { url, object_key } = presignedData;

      const uploadResponse = await axios.put(url, file, {
        headers: { "Content-Type": file.type },
        withCredentials: false,
        onUploadProgress: (progressEvent) => {
          const now = Date.now();
          const loaded = progressEvent.loaded;
          const total = progressEvent.total || 1;

          if (this._uploadStartTime === null) {
            this._uploadStartTime = now;
            this._uploadStartBytes = loaded;
          }

          const elapsedMs = now - this._uploadStartTime;
          const bytesTransferred = loaded - this._uploadStartBytes;
          const percent = Math.min(99, Math.round((loaded * 100) / total));

          let eta: number | null = this.uploadEtaSeconds;
          if (elapsedMs > 0 && bytesTransferred > 0) {
            const speedBps = (bytesTransferred * 1000) / elapsedMs;
            const remaining = total - loaded;
            const computed = Math.ceil(remaining / speedBps);
            if (computed > 0) {
              eta = computed;
            } else if (this._countdownInterval === null) {
              this._startCountdown();
            }
          }

          runInAction(() => {
            this.uploadProgress = percent;
            this.uploadEtaSeconds = eta;
          });
        },
      });

      if (uploadResponse.status !== 200) {
        throw new Error("Failed to upload file");
      }

      const updateResponse = await apiRequest(updateUploadVideoData, {
        method: "POST",
        body: JSON.stringify({
          object_key,
          title: fileTitle,
          size: file.size,
          duration_seconds: Math.round(durationSeconds),
          uuid,
          original_filename: file.name,
          transcription_engine: this.transcriptionEngine,
          audio_track: this.audioTrack,
          languages: this.languages,
          source: "web", // where this upload came from (library badge in the Premiere panel)
        }),
        headers: { "Content-Type": "application/json" },
      });

      if (updateResponse.status === 403) {
        throw await planLimitError(updateResponse);
      }

      if (!updateResponse.ok) {
        const errBody = await updateResponse
          .json()
          .catch(() => ({ message: "Failed to register upload" }));
        throw new Error(errBody.message || errBody.error || "Failed to register upload");
      }

      runInAction(() => {
        this.uploadProgress = 100;
      });

      onSuccess?.("Your video is being processed and will appear in your library ready for CherryPicking.");
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Upload failed";
      runInAction(() => {
        this.error = errorMessage;
        this.isUploading = false;
      });
      onError?.(errorMessage, (err as { limitLink?: LimitLink })?.limitLink);
    } finally {
      runInAction(() => {
        this.isUploading = false;
      });
    }
  };
}

const uploadStore = () => new UploadStore();

export default uploadStore;
